import { inject, Injectable } from "@angular/core";
import { EnvironmentConfig } from "@keus-template/environment";
import {
  CognitoUserPool,
  ICognitoUserPoolData,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import * as CognitoIdentity from "aws-sdk/clients/cognitoidentity";
import { CognitoIdentityCredentials } from "aws-sdk/global";
import { ServiceConfigurationOptions } from "aws-sdk/lib/service";

@Injectable({
  providedIn: "root",
})
export class CognitoUtilsService {
  private readonly environment = inject(EnvironmentConfig);

  private _cognitoCreds: CognitoIdentityCredentials | undefined;

  // This method takes in a raw jwtToken and uses the global AWS config options to build a
  // CognitoIdentityCredentials object and store it for us. It also returns the object to the caller
  // to avoid unnecessary calls to setCognitoCreds.
  buildCognitoCreds(idTokenJwt: string) {
    let url = `cognito-idp.${this.environment.cognito.region}.amazonaws.com/${this.environment.cognito.userPoolId}`;
    if (this.environment.cognito.idpEndpoint) {
      url = `${this.environment.cognito.idpEndpoint}/${this.environment.cognito.userPoolId}`;
    }

    const logins: CognitoIdentity.LoginsMap = {
      [url]: idTokenJwt,
    };
    const params: CognitoIdentityCredentials.CognitoIdentityOptions = {
      IdentityPoolId:
        this.environment.cognito.identityPoolId ?? "" /* required */,
      Logins: logins,
    };
    const serviceConfigs: ServiceConfigurationOptions = {};
    if (this.environment.cognito.identityEndpoint) {
      serviceConfigs.endpoint = this.environment.cognito.identityEndpoint;
    }
    const creds = new CognitoIdentityCredentials(params, serviceConfigs);
    this.cognitoCreds = creds;
    return creds;
  }

  getUserPool(): CognitoUserPool {
    const userPoolData: ICognitoUserPoolData = {
      UserPoolId: this.environment.cognito.userPoolId,
      ClientId: this.environment.cognito.userPoolClientId,
      endpoint: this.environment.cognito.identityEndpoint,
    };
    return new CognitoUserPool(userPoolData);
  }

  getCurrentUser() {
    return this.getUserPool().getCurrentUser();
  }
  
  /**
   * Get current user session from Cognito
   * Same pattern as Kosmo - retrieves token from memory/session
   * @returns Promise with the session or null if not authenticated
   */
  async getSession(): Promise<CognitoUserSession | null> {
    const cognitoUser = this.getCurrentUser();
    if (!cognitoUser) {
      return Promise.resolve(null);
    }

    return new Promise((resolve, reject) => {
      cognitoUser.getSession(
        (error: Error | null, session: CognitoUserSession | null) => {
          if (error) {
            reject(error);
          } else {
            resolve(session);
          }
        }
      );
    });
  }

  // AWS Stores Credentials in many ways, and with TypeScript this means that
  // getting the base credentials we authenticated with from the AWS globals gets really murky,
  // having to get around both class extension and unions. Therefore, we're going to give
  // developers direct access to the raw, unadulterated CognitoIdentityCredentials
  // object at all times.
  public get cognitoCreds(): CognitoIdentityCredentials | undefined {
    return this._cognitoCreds;
  }

  public set cognitoCreds(value: CognitoIdentityCredentials | undefined) {
    this._cognitoCreds = value;
  }
}
